import { DEAD_AIR_TOMBSTONE_TTL_MS } from "./deadAirConfig";

/**
 * Registry of dead-air classification correlationIds, in flight or recently completed.
 *
 * A dead-air ask reuses the normal expert-assist `orchestrator-invoke` path, so its reply arrives on
 * the same `gaia_ws_orchestrator-invoke-success` event the shared timeline listener
 * (`useGaiaMessageEvents`) renders. Registering the correlationId lets that listener skip the reply so
 * it never becomes a visible bubble — the dead-air request consumes the reply itself, matched by the
 * same correlationId, and decides whether anything is surfaced.
 *
 * The WebSocket request is already sent by the time it settles (via reply, timeout, or abort), so it
 * cannot be recalled — a slow-but-not-broken GAIA can still reply after we've stopped waiting. To keep
 * that late reply invisible too, a completed id is not deleted outright: it moves into a tombstone set
 * for `DEAD_AIR_TOMBSTONE_TTL_MS`, self-cleaning via `setTimeout` so the tombstone set never grows
 * unboundedly (at most a couple of entries at once, given the ask cooldown).
 */
const inFlightDeadAirCorrelationIds = new Set<string>();
const tombstonedDeadAirCorrelationIds = new Set<string>();
const tombstoneTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const registerDeadAirCorrelationId = (correlationId: string): void => {
    // Registering an id that is currently tombstoned (a fresh request reusing an id, in principle)
    // clears the stale tombstone so it doesn't expire out from under the new in-flight request.
    const pendingTombstoneTimer = tombstoneTimers.get(correlationId);
    if (pendingTombstoneTimer) {
        clearTimeout(pendingTombstoneTimer);
        tombstoneTimers.delete(correlationId);
    }
    tombstonedDeadAirCorrelationIds.delete(correlationId);

    inFlightDeadAirCorrelationIds.add(correlationId);
};

export const unregisterDeadAirCorrelationId = (correlationId: string): void => {
    // Only an id we were actually tracking as in-flight gets tombstoned. This also guards against
    // double-tombstoning: a second unregister call for the same id finds it already removed from the
    // in-flight set and returns early, so it can never schedule a second cleanup timer.
    if (!inFlightDeadAirCorrelationIds.delete(correlationId)) return;

    tombstonedDeadAirCorrelationIds.add(correlationId);
    const timer = setTimeout(() => {
        tombstonedDeadAirCorrelationIds.delete(correlationId);
        tombstoneTimers.delete(correlationId);
    }, DEAD_AIR_TOMBSTONE_TTL_MS);
    tombstoneTimers.set(correlationId, timer);
};

export const isDeadAirCorrelationId = (correlationId: string | undefined): boolean =>
    correlationId !== undefined &&
    (inFlightDeadAirCorrelationIds.has(correlationId) || tombstonedDeadAirCorrelationIds.has(correlationId));
